import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { getChartTruncationConfig } from '../../../utils/chartUtils';
import { useUserData } from '../../../hooks/useUserData';

const ExpenseProjectionChart = ({ expenses, projectionHorizon = 20 }) => {
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [inflationScenario, setInflationScenario] = useState('normal');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const { canProjectBeyond5Years } = useSubscription();
  const { userData } = useUserData();

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'medical', label: 'Medical' },
    { value: 'travel', label: 'Travel' },
    { value: 'personal', label: 'Personal' },
    { value: 'home', label: 'Home' },
    { value: 'food', label: 'Food' },
    { value: 'transport', label: 'Transport' },
    { value: 'kids', label: 'Kids' },
    { value: 'parents', label: 'Parents' }
  ];

  const inflationScenarios = [
    { value: 'normal', label: 'Normal Inflation (3.5%)' },
    { value: 'high', label: 'High Inflation (6%)' },
    { value: 'low', label: 'Low Inflation (2%)' }
  ];

  const generateProjectionData = () => {
    const currentYear = new Date().getFullYear();
    const data = [];
    const safeHorizon = Math.max(1, Math.min(60, Number(projectionHorizon) || 20));
    
    const inflationRates = {
      'normal': 3.5,
      'high': 6.0,
      'low': 2.0
    };

    for (let year = currentYear; year <= currentYear + safeHorizon; year++) {
      const yearData = { year };
      
      categories.forEach(category => {
        if (category.value === 'all') return;
        
        const categoryExpenses = expenses.filter(expense => 
          expense.category === category.value &&
          year >= expense.startYear &&
          year <= expense.endYear
        );

        let totalExpense = 0;
        categoryExpenses.forEach(expense => {
          const multiplier = {
            'monthly': 12,
            'quarterly': 4,
            'annually': 1,
            'one-time': 1
          }[expense.frequency] || 1;

          const yearsFromStart = year - expense.startYear;
          const inflationRate = inflationScenario === 'normal' ? expense.inflationRate : inflationRates[inflationScenario];
          const inflationFactor = Math.pow(1 + inflationRate / 100, yearsFromStart);
          
          // Age-based modifications
          let ageAdjustment = 1;
          const userAge = 35 + (year - currentYear); // Assuming user is 35 now
          
          if (category.value === 'travel' && userAge > 75) {
            ageAdjustment = 0.3; // 70% reduction in travel expenses after 75
          }
          
          if (category.value === 'kids' && userAge > 60) {
            ageAdjustment = 0; // Education costs end
          }

          totalExpense += expense.amount * multiplier * inflationFactor * ageAdjustment * (expense.probability / 100);
        });

        yearData[category.value] = totalExpense;
      });

      // Calculate total
      yearData.total = categories.reduce((sum, cat) => {
        if (cat.value === 'all') return sum;
        return sum + (yearData[cat.value] || 0);
      }, 0);

      data.push(yearData);
    }

    return data;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ scenario: inflationScenario });
        const demoParams = getDemoQueryParams();
        const separator = params.toString() ? '&' : '';
        const fullParams = `${params.toString()}${demoParams ? separator + demoParams.substring(1) : ''}`;
        const res = await fetch(buildApiUrl(`/api/expense/projections?${fullParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load expense projections');
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) {
          setChartData(json.data);
        } else {
          setChartData(generateProjectionData()); // fallback
        }
      } catch (e) {
        console.error(e);
        setChartData(generateProjectionData()); // fallback
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inflationScenario, expenses, projectionHorizon, getDemoQueryParams, getAuthHeaders]);

  const categoryColors = {
    medical: '#ef4444',
    travel: '#3b82f6',
    personal: '#8b5cf6',
    home: '#10b981',
    food: '#f97316',
    transport: '#6366f1',
    kids: '#ec4899',
    parents: '#14b8a6',
    total: '#1f2937'
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const nonZero = payload.filter((p) => Number(p.value) !== 0);
      if (nonZero.length === 0) return null;
      return (
        <div
          className="bg-card border border-border rounded-lg p-3"
          style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
        >
          <p className="text-sm font-medium text-foreground">{`Year ${label}`}</p>
          {nonZero.map((entry, idx) => (
            <p key={idx} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatCurrency(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Expense Projection</h3>
          <p className="text-sm text-muted-foreground">{`${Math.max(1, Math.min(60, Number(projectionHorizon) || 20))}-year expense forecast with inflation adjustments`}</p>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <Select
            options={inflationScenarios}
            value={inflationScenario}
            onChange={setInflationScenario}
            className="w-48"
          />
        </div>
      </div>

      <div className="relative" style={{ height: '320px', marginBottom: '1.5rem' }}>
        {(() => {
          const currentYear = new Date().getFullYear();
          const fullTotalYears = chartData.length;
          const truncationConfig = getChartTruncationConfig('ExpenseProjection', canProjectBeyond5Years, isDemoModeActive);
          const shouldTruncate = truncationConfig.shouldTruncate;
          const freeYearsLimit = 5;
          const displayYearsLimit = freeYearsLimit * 2; // 10 years for free users only
          const rawData = shouldTruncate ? chartData.slice(0, displayYearsLimit + 1) : chartData;
          const displayData = rawData.map((row, idx) => ({
            ...row,
            isBlurred: shouldTruncate && idx > freeYearsLimit
          }));
          
          // Show year labels every 2 years
          const xAxisTicks = [];
          displayData.forEach((d, i) => {
            if (i % 2 === 0) {
              xAxisTicks.push(d.year);
            }
          });

          return (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={displayData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    {/* Gradient for total line */}
                    <linearGradient id="blurGradientTotal" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={categoryColors.total} stopOpacity="1" />
                      <stop offset="48%" stopColor={categoryColors.total} stopOpacity="1" />
                      <stop offset="55%" stopColor={categoryColors.total} stopOpacity="0.4" />
                      <stop offset="100%" stopColor={categoryColors.total} stopOpacity="0.15" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="year" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    ticks={xAxisTicks}
                    height={50}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip 
                    content={(props) => {
                      if (props.active && props.payload && props.payload.length) {
                        const isBlurred = props.payload[0]?.payload?.isBlurred;
                        if (isBlurred) {
                          return (
                            <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                              <p className="font-medium text-muted-foreground flex items-center gap-2">
                                <Icon name="Lock" size={14} />
                                Locked Content
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Upgrade to view full projections
                              </p>
                            </div>
                          );
                        }
                        return <CustomTooltip {...props} />;
                      }
                      return null;
                    }}
                  />
                  <Legend />

                  {/* Always plot total plus each category line */}
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke={shouldTruncate ? 'url(#blurGradientTotal)' : categoryColors.total}
                    strokeWidth={3}
                    dot={false}
                    name="Total Expenses"
                    strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                  />

                  {categories
                    .filter(c => c.value !== 'all')
                    .map(c => (
                      <Line
                        key={c.value}
                        type="monotone"
                        dataKey={c.value}
                        stroke={categoryColors[c.value]}
                        strokeWidth={2}
                        dot={false}
                        name={c.label}
                        strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                        strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
              
              {/* Blurred overlay with gradient and CTA */}
              {shouldTruncate && fullTotalYears > freeYearsLimit && (
                <div 
                  className="absolute top-0 right-0 pointer-events-none"
                  style={{ 
                    left: '48%',
                    bottom: '50px',
                    background: 'linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 8%, rgba(255, 255, 255, 0.85) 25%, rgba(255, 255, 255, 0.95) 60%)',
                    backdropFilter: 'blur(5px)',
                  }}
                >
                  {/* Dark mode gradient overlay */}
                  <div 
                    className="absolute inset-0 dark:block hidden"
                    style={{ 
                      background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 8%, rgba(0, 0, 0, 0.75) 25%, rgba(0, 0, 0, 0.9) 60%)',
                    }}
                  />
                  
                  {/* Upgrade CTA Card */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-2xl p-4 w-[180px] text-white">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                          <Icon name="Lock" size={22} className="text-white" />
                        </div>
                      </div>
                      <h4 className="font-bold text-center mb-1 text-sm">
                        {(userData?.setup?.projectionHorizon || 20) - freeYearsLimit}+ Years Locked
                      </h4>
                      <p className="text-xs text-center text-white/90 mb-3 leading-tight">
                        Expenses accelerate here. See the full impact.
                      </p>
                      <a
                        href="/upgrade"
                        className="block w-full bg-white text-amber-600 font-semibold text-sm py-2 px-3 rounded-lg text-center hover:bg-amber-50 transition-colors shadow-md"
                      >
                        See My Full Picture
                      </a>
                    </div>
                  </div>
                  
                  {/* Vertical separator line at truncation point */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-amber-400 to-transparent opacity-70"
                    style={{ 
                      boxShadow: '0 0 12px rgba(251, 191, 36, 0.7)'
                    }}
                  />
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className={`grid gap-4 ${canProjectBeyond5Years ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'}`}>
        <div className="bg-muted p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">Current Year</p>
          <p className="text-xl font-semibold text-foreground">
            {formatCurrency(chartData[0]?.total || 0)}
          </p>
        </div>
        
        <div className="bg-muted p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">{canProjectBeyond5Years ? 'Year 10' : 'Year 5'}</p>
          <p className="text-xl font-semibold text-foreground">
            {formatCurrency(chartData[canProjectBeyond5Years ? 10 : 5]?.total || 0)}
          </p>
        </div>
        
        {canProjectBeyond5Years && (
          <>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">Year {Math.max(1, Math.min(60, Number(projectionHorizon) || 20))}</p>
              <p className="text-xl font-semibold text-foreground">
                {formatCurrency(chartData[Math.max(0, Math.min(chartData.length - 1, Number(projectionHorizon) || 20))]?.total || 0)}
              </p>
            </div>
            
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">Total Projected</p>
              <p className="text-xl font-semibold text-accent">
                {formatCurrency(chartData.reduce((sum, year) => sum + (year.total || 0), 0))}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExpenseProjectionChart;